import React, { useState } from "react";
import { compose, graphql } from "react-apollo";
import { useQuery, useMutation } from "@apollo/react-hooks";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import Table from "components/Table/Table";
import { withQuery } from "containers/withQuery";
import PencilButton from "components/ActionButtons/PencilButton";
import DeleteButton from "components/ActionButtons/DeleteButton";
import AlertModal from "components/AlertModal/AlertModal";
import translateErrorMessage from "utils/translateErrorMessage";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import { httpClient } from "settings/apolloClient.jsx";
import { USERS_QUERY, CURRENT_OPERATOR_QUERY, CURRENT_USER_ROLE_QUERY } from "./userManagementQueries";
import {
  DELETE_USER_MUTATION,
  ASSIGN_AUTOMATION_OPERATOR_MUTATION,
  CLEAR_AUTOMATION_OPERATOR_MUTATION,
} from "./userManagementMutations";
import { userRoleLabels } from "./roleTypes";
import UserEdit from "./UserEdit";

const headers = [
  { label: "Id", key: "id" },
  { label: "Nome", key: "name" },
  { label: "Email", key: "email" },
  { label: "Papel", key: "role" },
  { label: "Em comando", key: "command" },
  { label: "", key: "action" },
  { label: "", key: "delete" },
];

const UsersTable = ({ users, deleteUser, snackbarContext }) => {
  const [editUser, setEditUser] = useState(null);

  // The app's <ApolloProvider> (react-apollo 2.5) does not share context with the
  // @apollo/react-hooks client, so every hook must receive the client explicitly.
  const { data: operatorData, refetch: refetchOperator } = useQuery(
    CURRENT_OPERATOR_QUERY,
    { fetchPolicy: "cache-and-network", client: httpClient }
  );
  const [assignOperator] = useMutation(ASSIGN_AUTOMATION_OPERATOR_MUTATION, { client: httpClient });
  const [clearOperator] = useMutation(CLEAR_AUTOMATION_OPERATOR_MUTATION, { client: httpClient });

  const { data: meData } = useQuery(CURRENT_USER_ROLE_QUERY, { fetchPolicy: "cache-first", client: httpClient });
  const me = meData && meData.getCurrentUser;
  const actingRole = me && me.role;
  const isSelf = user => Boolean(me) && user.id === me.id;
  const isUnprotected = user => user.role !== "ADMIN" && user.role !== "SUPERUSER";
  // An admin may edit itself and non-admins, but not other admins/superusers.
  const canEdit = user => actingRole === "SUPERUSER" || isSelf(user) || isUnprotected(user);
  // Nobody deletes their own account; an admin also cannot delete admins/superusers.
  const canDelete = user => (actingRole === "SUPERUSER" || isUnprotected(user)) && !isSelf(user);

  const currentOperator = operatorData && operatorData.getCurrentAutomationOperator;

  const closeEdit = () => setEditUser(null);

  const handleDeleteUser = async (id) => {
    try {
      await deleteUser({
        variables: { id },
        refetchQueries: [{ query: USERS_QUERY }],
      });

      snackbarContext.setNotificationTimeOut(Color.success, "Usuário deletado.");
    } catch (error) {
      snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
    }
  };

  const handleDeleteModal = (user) => {
    AlertModal.fire({
      title: "Deletar usuário",
      html: `Deseja deletar o usuário "${user.name}"?`,
      type: "error",
      showCancelButton: true,
      confirmButtonColor: "#1D84C6",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Voltar",
      reverseButtons: true,
    }).then(({ value }) => {
      if (value) {
        handleDeleteUser(user.id);
      }
    });
  };

  const doAssign = async (user) => {
    try {
      await assignOperator({ variables: { userId: user.id } });
      await refetchOperator();
      snackbarContext.setNotificationTimeOut(Color.success, `${user.name} está em comando.`);
    } catch (error) {
      snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
    }
  };

  const doClear = async () => {
    try {
      await clearOperator();
      await refetchOperator();
      snackbarContext.setNotificationTimeOut(Color.success, "Comando liberado.");
    } catch (error) {
      snackbarContext.setNotificationTimeOut(Color.danger, translateErrorMessage(error));
    }
  };

  const handleToggleCommand = (user) => {
    const isCurrent = currentOperator && currentOperator.id === user.id;

    if (isCurrent) {
      doClear();
      return;
    }

    // Takeover: another user is in command → confirm before switching.
    if (currentOperator) {
      AlertModal.fire({
        title: "Assumir comando",
        html: `"${currentOperator.name}" está em comando. Colocar "${user.name}" no comando no lugar?`,
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1D84C6",
        confirmButtonText: "Assumir",
        cancelButtonText: "Voltar",
        reverseButtons: true,
      }).then(({ value }) => {
        if (value) {
          doAssign(user);
        }
      });
      return;
    }

    doAssign(user);
  };

  const buildTableItems = () => (
    [...users]
      .sort((a, b) => a.id - b.id)
      .map(user => ({
        ...user,
        name: isSelf(user) ? `${user.name || ""} (você)` : user.name,
        role: userRoleLabels[user.role] || user.role,
        command: (
          <Toggle
            checked={!!currentOperator && currentOperator.id === user.id}
            icons={{ unchecked: null }}
            onChange={() => handleToggleCommand(user)}
          />
        ),
        action: canEdit(user)
          ? <PencilButton onPencilEditClick={() => setEditUser(user)} />
          : null,
        delete: canDelete(user)
          ? <DeleteButton onClick={() => handleDeleteModal(user)} />
          : null,
      }))
  );

  return (
    <React.Fragment>
      <Table
        headers={headers}
        matchHeaders
        items={buildTableItems()}
      />

      {editUser && (
        <UserEdit
          user={editUser}
          isOpen
          onFinishEdit={closeEdit}
        />
      )}
    </React.Fragment>
  );
};

export default compose(
  withQuery({ query: USERS_QUERY }),
  graphql(DELETE_USER_MUTATION, { name: "deleteUser", }),
  withSnackbarConsumer,
)(UsersTable);
